// Embeddable paid-ads lead form (form body only — the host landing page owns
// its own headline, imagery and layout).
//
// It is the contact form's sibling, not a variant of it. A landing page bought
// the visit, so the form asks for the least it can and promises a call back:
// name and phone are required, email is optional, and there is no message box.
// Posts the same PublicContactZ payload with `kind: 'callback'`, which is what
// relaxes email/body server-side while requiring the number — and what picks
// the call-back notification pair (`callback_requested_*`) over the enquiry
// one, so the front desk gets a call sheet rather than an empty "message".
//
// The marketing opt-in is the same wording, versioned server-side as
// contact.form.marketingOptIn@v1 — but it is an opt-in to *email*, so it is
// only offered once there is an address to consent for.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { AlertCircle, Loader2, Phone } from 'lucide-react';
import { EmbedConfirmation } from './confirmation';
import { Turnstile } from '../../src/components/security/TurnstileLite.client';
import { PHONE_RE } from '../../src/lib/format/phone';
import type { Locale } from '../../src/i18n/config';

interface PublicClinic {
  id: string;
  slug: string | null;
  display_name: string;
}

export interface LeadSuccessDetail {
  clinicId: string;
  clinicSlug: string | null;
  /** Absent when the patient left the optional field blank. */
  email?: string;
  phone: string;
  marketingOptIn: boolean;
  /** Campaign label the host page passed in, for its own conversion tagging. */
  subject?: string;
}

export interface LeadFormProps {
  baseUrl: string;
  locale: Locale;
  clinics: PublicClinic[];
  /** uuid or slug preselect (element attribute or host ?clinica=/?clinic=). */
  preselectClinic?: string;
  turnstileSiteKey?: string;
  theme: string;
  /** Recorded on the row, e.g. `lp:consulta-dentaria`. */
  source: string;
  /** Human-readable campaign name shown to the practice, e.g. "Implantes". */
  subject?: string;
  /** Host's own CTA wording, so each landing page keeps its own promise. */
  submitLabel?: string;
  /** Ad attribution the host page captured from its URL (gclid, utm_*). */
  attribution?: Record<string, string>;
  onStart?: () => void;
  onSuccess?: (detail: LeadSuccessDetail) => void;
  onError?: (code: string) => void;
}

type Status = 'idle' | 'sending' | 'success' | 'error';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LeadFormEmbed({
  baseUrl,
  locale,
  clinics,
  preselectClinic,
  turnstileSiteKey,
  theme,
  source,
  subject,
  submitLabel,
  attribution,
  onStart,
  onSuccess,
  onError,
}: LeadFormProps) {
  const t = useTranslations('contact.form');
  const tv = useTranslations('contact.validation');
  const te = useTranslations('contact.errors');

  const preselected = useMemo(() => {
    if (!preselectClinic) return undefined;
    return clinics.find((c) =>
      UUID_RE.test(preselectClinic)
        ? c.id.toLowerCase() === preselectClinic.toLowerCase()
        : c.slug === preselectClinic,
    );
  }, [clinics, preselectClinic]);

  const [clinicId, setClinicId] = useState<string>(
    preselected?.id ?? (clinics.length === 1 ? clinics[0]!.id : ''),
  );
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  // Errors double as the record of what has been checked: a field is only in
  // here once the patient has left it or pressed submit. Validating on
  // keystroke would tell someone their email is wrong while they type it.
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  const startedRef = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const idemKeyRef = useRef<string>(crypto.randomUUID());

  const captchaRequired = Boolean(turnstileSiteKey);
  const emailGiven = EMAIL_RE.test(email.trim());

  const onFirstInteraction = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    onStart?.();
  }, [onStart]);

  useEffect(() => {
    if (preselected) setClinicId(preselected.id);
  }, [preselected]);

  const fieldErrors = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (name.trim().length < 2) next.name = tv('nameRequired');
    if (!PHONE_RE.test(phone.trim())) next.phone = tv('phoneRequired');
    if (email.trim() && !EMAIL_RE.test(email.trim())) next.email = tv('emailInvalid');
    // The box is always offered, but it is an opt-in to *email*: ticking it
    // without an address would be a consent the send gate could never act on,
    // and the API rejects exactly that. So the tick makes the optional field
    // required rather than being quietly dropped — the patient asked for
    // something, and they get told what it needs instead of nothing happening.
    else if (marketingOptIn && !emailGiven) next.email = tv('emailForMarketing');
    if (!clinicId) next.clinic = tv('clinicRequired');
    if (!consent) next.consent = tv('consentRequired');
    return next;
  };

  /** Show a field's error once the patient has moved on from it. */
  const blur = (field: string) => {
    const all = fieldErrors();
    setErrors((prev) => ({ ...prev, [field]: all[field] ?? '' }));
  };

  const validate = (): boolean => {
    const next = fieldErrors();
    setErrors(next);
    // WCAG: send focus to the first thing that needs fixing, rather than
    // leaving the patient to hunt for red text on a form they just submitted.
    const first = ['name', 'phone', 'email', 'clinic', 'consent'].find((f) => next[f]);
    if (first) {
      formRef.current
        ?.querySelector<HTMLElement>(`[data-field="${first}"]`)
        ?.focus();
    }
    return Object.keys(next).length === 0;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!validate()) return;
    if (captchaRequired && !turnstileToken) {
      setStatus('error');
      setErrorCode('captchaPending');
      onError?.('captcha_pending');
      return;
    }
    setStatus('sending');
    setErrorCode(null);
    const trimmedEmail = email.trim();
    try {
      const res = await fetch(`${baseUrl}/api/public/contact`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          kind: 'callback',
          clinicId,
          locale,
          name: name.trim(),
          phone: phone.trim(),
          ...(trimmedEmail ? { email: trimmedEmail } : {}),
          ...(subject ? { subject } : {}),
          source,
          // Only claim an opt-in when there is an address it can apply to.
          consent: {
            rgpd: true,
            ...(trimmedEmail ? { marketing: { email: marketingOptIn } } : {}),
          },
          ...(attribution && Object.keys(attribution).length
            ? { metadata: attribution }
            : {}),
          idempotencyKey: idemKeyRef.current,
          ...(turnstileToken ? { turnstileToken } : {}),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        const code =
          res.status === 429
            ? 'rateLimited'
            : body.error === 'captcha_failed'
              ? 'captchaFailed'
              : 'generic';
        setStatus('error');
        setErrorCode(code);
        onError?.(body.error ?? `http_${res.status}`);
        setTurnstileToken('');
        setTurnstileNonce((n) => n + 1);
        return;
      }
      const clinic = clinics.find((c) => c.id === clinicId);
      setStatus('success');
      onSuccess?.({
        clinicId,
        clinicSlug: clinic?.slug ?? null,
        ...(trimmedEmail ? { email: trimmedEmail } : {}),
        phone: phone.trim(),
        marketingOptIn: Boolean(trimmedEmail && marketingOptIn),
        subject,
      });
      idemKeyRef.current = crypto.randomUUID();
    } catch {
      setStatus('error');
      setErrorCode('generic');
      onError?.('network');
      setTurnstileToken('');
      setTurnstileNonce((n) => n + 1);
    }
  };

  if (status === 'success') {
    return (
      <EmbedConfirmation theme={theme} title={t('leadSuccessTitle')} body={t('leadSuccessBody')} />
    );
  }

  return (
    <div className="booking-widget booking-widget--bare" data-bw-theme={theme}>
      <form
        ref={formRef}
        className="patient-form embed-card"
        noValidate
        onSubmit={submit}
        onFocus={onFirstInteraction}
        aria-busy={status === 'sending'}
      >
        {status === 'error' && (
          <div className="booking-error booking-error--icon" role="alert">
            <AlertCircle aria-hidden />
            <div>
              <p>{t('errorTitle')}</p>
              <p>{errorCode ? te(errorCode) : te('generic')}</p>
            </div>
          </div>
        )}

        {/* One field per row. The contact form pairs fields two-up because it
            sits on a wide page; this one lives in a narrow landing-page card,
            where a pair means two half-width inputs and a clinic name clipped
            mid-word. Stacked, each field gets the full width and the eye has a
            single path down to the button. */}
        <label className="patient-form-field">
          <span className="patient-form-label">{t('name')} *</span>
          <input
            required
            autoComplete="name"
            placeholder={t('namePlaceholder')}
            className={`patient-form-input${errors.name ? ' is-invalid' : ''}`}
            data-field="name"
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'name-error' : undefined}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => blur('name')}
          />
          {errors.name && (
            <p className="patient-form-error" id="name-error" role="alert">
              {errors.name}
            </p>
          )}
        </label>

        <label className="patient-form-field">
          <span className="patient-form-label">{t('phone')} *</span>
          <input
            required
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder={t('phonePlaceholder')}
            className={`patient-form-input${errors.phone ? ' is-invalid' : ''}`}
            data-field="phone"
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={errors.phone ? 'phone-error' : undefined}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onBlur={() => blur('phone')}
          />
          {errors.phone && (
            <p className="patient-form-error" id="phone-error" role="alert">
              {errors.phone}
            </p>
          )}
        </label>

        <label className="patient-form-field">
          <span className="patient-form-label">{t('emailOptional')}</span>
          <input
            type="email"
            autoComplete="email"
            placeholder={t('emailPlaceholder')}
            className={`patient-form-input${errors.email ? ' is-invalid' : ''}`}
            data-field="email"
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'email-error' : undefined}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => blur('email')}
          />
          {errors.email && (
            <p className="patient-form-error" id="email-error" role="alert">
              {errors.email}
            </p>
          )}
        </label>

        {!preselected && clinics.length > 1 && (
          <label className="patient-form-field">
            <span className="patient-form-label">{t('clinic')} *</span>
            <select
              required
              className={`patient-form-input${errors.clinic ? ' is-invalid' : ''}`}
              data-field="clinic"
              aria-invalid={Boolean(errors.clinic)}
              aria-describedby={errors.clinic ? 'clinic-error' : undefined}
              value={clinicId}
              onChange={(e) => setClinicId(e.target.value)}
              onBlur={() => blur('clinic')}
            >
              <option value="" disabled>
                {t('clinicPlaceholder')}
              </option>
              {clinics.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name}
                </option>
              ))}
            </select>
            {errors.clinic && (
              <p className="patient-form-error" id="clinic-error" role="alert">
                {errors.clinic}
              </p>
            )}
          </label>
        )}

        <div className={`checkbox-container${errors.consent ? ' is-invalid' : ''}`}>
          <input
            id="lead-consent"
            type="checkbox"
            data-field="consent"
            aria-invalid={Boolean(errors.consent)}
            aria-describedby={errors.consent ? 'consent-error' : undefined}
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            onBlur={() => blur('consent')}
          />
          <div>
            <label htmlFor="lead-consent" className="cursor-pointer text-sm">
              {t('consent')}
            </label>
            {errors.consent && (
              <p className="patient-form-error" id="consent-error" role="alert">
                {errors.consent}
              </p>
            )}
          </div>
        </div>

        <div className="checkbox-container">
          <input
            id="lead-marketing"
            type="checkbox"
            checked={marketingOptIn}
            onChange={(e) => setMarketingOptIn(e.target.checked)}
          />
          <label htmlFor="lead-marketing" className="cursor-pointer text-sm">
            {t('marketingOptIn')}
          </label>
        </div>

        {turnstileSiteKey && (
          <Turnstile
            key={turnstileNonce}
            siteKey={turnstileSiteKey}
            locale={locale}
            onToken={setTurnstileToken}
          />
        )}

        <button
          type="submit"
          /* Only disabled while the request is in flight. Greying it out until
             Turnstile resolves left a dead button with nothing saying why —
             pressing it now surfaces "finish the check first", which is a
             sentence rather than a guess. */
          disabled={status === 'sending'}
          className="cta-primary cta-primary--fit"
        >
          {status === 'sending' ? (
            <Loader2 className="cta-primary-icon animate-spin" aria-hidden />
          ) : (
            <Phone className="cta-primary-icon" aria-hidden />
          )}
          {status === 'sending' ? t('sending') : (submitLabel ?? t('leadSubmit'))}
        </button>
      </form>
    </div>
  );
}
